// File: webelieve-frontend/src/features/intake/api/edgeflowApi.ts
// M7.30: Refactored to consume the supervisory backend surface from M7.29.
// The dashboard metrics endpoint now returns both volume/trend data and
// supervisory operational posture in a single response.

import { apiClient } from '@/config/api';
import type { IntakeSubmission } from '@/config/api';

// Legacy submission type (kept for backward compatibility with workflow active endpoint)
export interface EdgeFlowSubmission extends IntakeSubmission {
  ai_analysis?: {
    lead_score?: number;
    confidence?: number;
    recommended_action?: string;
    conflict_detected?: boolean;
    practice_area?: string;
    urgency_level?: 'low' | 'medium' | 'high' | 'critical';
    processing_status?: 'pending' | 'analyzing' | 'complete' | 'error';
  };
  processing_metrics?: {
    processing_time_ms?: number;
    api_calls?: number;
    ai_model_used?: string[];
    confidence_scores?: Record<string, number>;
  };
}

// ---------------------------------------------------------------------------
// M7.29/30: Supervisory types consumed from the backend
// ---------------------------------------------------------------------------

export interface SupervisoryActionableQueue {
  queue_name: string;
  count: number;
  severity: 'urgent' | 'elevated' | 'attention' | 'info';
  description: string;
}

export interface SupervisoryStageEntry {
  stage: string;
  count: number;
  stuck_count: number;
}

export interface SupervisoryPosture {
  total_active_leads: number;
  stage_bottleneck: string | null;
  stage_bottleneck_count: number;
  actionable_queues: SupervisoryActionableQueue[];
  stuck_leads_count: number;
  stale_leads_count: number;
  missing_conflict_check_count: number;
  missing_documents_count: number;
  retainer_sent_unsigned_count: number;
  unresolved_workflow_failures_count: number;
  high_urgency_unreviewed_count: number;
  review_required_count: number;
}

export interface DashboardMetrics {
  summary: {
    totalLeads: number;
    totalQualified: number;
    totalConverted: number;
    conversionRate: number;
    avgTimeToConversion: number;
    avgLeadScore: number;
  };
  byStage: Record<string, number>;
  bySource: Record<string, { count: number; percentage: number }>;
  trends: {
    daily: Array<{ date: string; leads: number; qualified: number; converted: number }>;
  };
  supervisory: SupervisoryPosture;
}

export interface CohortLead {
  client_id: string;
  client_name: string;
  email: string | null;
  phone: string | null;
  current_stage: string | null;
  status: string;
  source: string | null;
  practice_area: string | null;
  assigned_to_user_id: string | null;
  has_intake_documents: boolean;
  document_count: number;
  recommended_next_action: string | null;
  lead_created_at: string;
}

export interface CohortResult {
  queue_name: string;
  description: string;
  total: number;
  leads: CohortLead[];
}

// Legacy types for backward compat
export interface EdgeFlowMetrics {
  total_submissions: number;
  pending_review: number;
  completed_today: number;
  conversion_rate: number;
  average_processing_time: number;
  ai_confidence_avg: number;
  submissions_by_status: Record<string, number>;
  submissions_by_hour: Array<{ hour: string; count: number }>;
}

export interface EdgeFlowQueue {
  high_priority: EdgeFlowSubmission[];
  normal_priority: EdgeFlowSubmission[];
  review_required: EdgeFlowSubmission[];
  completed_recent: EdgeFlowSubmission[];
}

export const edgeflowApi = {
  /**
   * M7.30: Get unified dashboard data including supervisory posture.
   * The backend /dashboard/metrics endpoint now returns both volume metrics
   * and supervisory operational intelligence in one call.
   */
  getDashboardData: async (): Promise<{
    metrics: EdgeFlowMetrics;
    queue: EdgeFlowQueue;
    dashboardMetrics: DashboardMetrics | null;
  }> => {
    try {
      const [metricsResponse, activeWorkflowsResponse] = await Promise.all([
        apiClient.get('/api/v1/intake/dashboard/metrics'),
        apiClient.get('/api/v1/intake/workflow/active'),
      ]);

      if (!metricsResponse.success) throw new Error('Failed to fetch dashboard metrics');
      if (!activeWorkflowsResponse.success) throw new Error('Failed to fetch active workflows');

      const backendMetrics = (metricsResponse.data as any)?.metrics || {};
      const activeWorkflows = (activeWorkflowsResponse.data as any)?.workflows || [];

      // Full dashboard metrics including supervisory posture
      const dashboardMetrics: DashboardMetrics = {
        summary: backendMetrics.summary || {},
        byStage: backendMetrics.byStage || {},
        bySource: backendMetrics.bySource || {},
        trends: backendMetrics.trends || { daily: [] },
        supervisory: backendMetrics.supervisory || {
          total_active_leads: 0,
          stage_bottleneck: null,
          stage_bottleneck_count: 0,
          actionable_queues: [],
          stuck_leads_count: 0,
          stale_leads_count: 0,
          missing_conflict_check_count: 0,
          missing_documents_count: 0,
          retainer_sent_unsigned_count: 0,
          unresolved_workflow_failures_count: 0,
          high_urgency_unreviewed_count: 0,
          review_required_count: 0,
        },
      };

      // Legacy metrics shape (backward-compat for components not yet refactored)
      const metrics: EdgeFlowMetrics = {
        total_submissions: backendMetrics.summary?.totalLeads || 0,
        pending_review: dashboardMetrics.supervisory.review_required_count || 0,
        completed_today: backendMetrics.summary?.totalConverted || 0,
        conversion_rate: backendMetrics.summary?.conversionRate || 0,
        average_processing_time: backendMetrics.aiMetrics?.avgProcessingTime || 0,
        ai_confidence_avg: backendMetrics.aiMetrics?.aiAccuracy || 0,
        submissions_by_status: backendMetrics.byStage || {},
        submissions_by_hour: backendMetrics.trends?.daily?.map((day: any) => ({
          hour: new Date(day.date).getHours().toString().padStart(2, '0') + ':00',
          count: day.leads || 0,
        })) || [],
      };

      // Workflow queue
      const queue: EdgeFlowQueue = {
        high_priority: activeWorkflows.filter((wf: any) =>
          wf.status === 'processing' && wf.elapsed > 300000,
        ).map(transformWorkflowToSubmission),
        normal_priority: activeWorkflows.filter((wf: any) =>
          wf.status === 'processing' && wf.elapsed <= 300000,
        ).map(transformWorkflowToSubmission),
        review_required: activeWorkflows.filter((wf: any) =>
          wf.status === 'human_review_needed',
        ).map(transformWorkflowToSubmission),
        completed_recent: activeWorkflows.filter((wf: any) =>
          wf.status === 'completed',
        ).slice(0, 5).map(transformWorkflowToSubmission),
      };

      return { metrics, queue, dashboardMetrics };
    } catch (error) {
      console.error('EdgeFlow API Error:', error);
      throw error;
    }
  },

  /**
   * M7.30: Fetch the cohort of leads behind a specific supervisory queue.
   * Uses GET /api/v1/intake/dashboard/cohort/:queueName
   */
  getCohort: async (queueName: string, options?: {
    limit?: number;
    offset?: number;
    workflow_failed_after?: string;
    retainer_event_after?: string;
  }): Promise<CohortResult> => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.workflow_failed_after) params.set('workflow_failed_after', options.workflow_failed_after);
    if (options?.retainer_event_after) params.set('retainer_event_after', options.retainer_event_after);

    const url = `/api/v1/intake/dashboard/cohort/${queueName}${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.get(url);

    if (!response.success || !response.data) {
      throw new Error(`Failed to fetch cohort: ${queueName}`);
    }

    return response.data as CohortResult;
  },

  // Legacy methods (preserved)
  getSubmissionDetails: async (submissionId: string): Promise<EdgeFlowSubmission> => {
    const response = await apiClient.get(`/api/v1/intake/workflow/status/${submissionId}`);
    if (!response.success || !response.data) throw new Error(`Failed to fetch submission ${submissionId}`);
    return transformWorkflowToSubmission(response.data);
  },

  updateSubmissionStatus: async (
    submissionId: string,
    status: EdgeFlowSubmission['status'],
    notes?: string,
  ): Promise<EdgeFlowSubmission> => {
    const response = await apiClient.post('/api/v1/intake/pipeline/move', { submissionId, targetStage: status, notes });
    if (!response.success || !response.data) throw new Error(`Failed to update submission ${submissionId}`);
    return transformWorkflowToSubmission(response.data);
  },

  triggerReanalysis: async (submissionId: string): Promise<EdgeFlowSubmission> => {
    const response = await apiClient.post('/api/v1/intake/pipeline/move', {
      submissionId, targetStage: 'processing', notes: 'Manual reanalysis requested',
    });
    if (!response.success || !response.data) throw new Error(`Failed to reanalyze submission ${submissionId}`);
    return transformWorkflowToSubmission(response.data);
  },
};

function transformWorkflowToSubmission(workflow: any): EdgeFlowSubmission {
  return {
    id: workflow.submissionId || workflow.id,
    submissionId: workflow.submissionId || workflow.id,
    created_at: workflow.startTime || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    status: mapWorkflowStatusToSubmissionStatus(workflow.status),
    form_id: 'intake-form',
    realm_id: workflow.realmId || '',
    formData: { currentStep: workflow.currentStep, completedSteps: workflow.completedSteps, totalSteps: workflow.totalSteps },
    submission_data: { currentStep: workflow.currentStep, completedSteps: workflow.completedSteps, totalSteps: workflow.totalSteps },
    language: 'en',
    source: 'web',
    ai_analysis: {
      processing_status: workflow.status === 'processing' ? 'analyzing' : 'complete',
      urgency_level: workflow.elapsed > 300000 ? 'high' : 'normal' as any,
      recommended_action: workflow.status === 'human_review_needed' ? 'human_review' : 'auto_process',
    },
    processing_metrics: { processing_time_ms: workflow.elapsed || 0, api_calls: workflow.completedSteps || 0 },
  } as EdgeFlowSubmission;
}

function mapWorkflowStatusToSubmissionStatus(workflowStatus: string): string {
  const statusMap: Record<string, string> = {
    'processing': 'processing', 'completed': 'completed', 'human_review_needed': 'review_required',
    'error': 'error', 'pending': 'submitted',
  };
  return statusMap[workflowStatus] || 'submitted';
}
